import { Suspense } from "react"
import { Navigate, useLocation } from "react-router-dom"
import InfiniteScroll from "react-infinite-scroller"
import { Loading, Icon } from "source/shared/components"
import { useQRCode } from "source/shared/QRCode"
import { useSession } from "source/shared/hooks/useSession"
import { ApplicantAlerts } from "source/groups/components/ApplicantAlerts"
import {
  useInfiniteLoadingGroupTypes,
  useFirstPageOfGroupTypes,
} from "source/groups/hooks/useGroupTypes"
import { MyGroupsHeader } from "./components/MyGroupsHeader"
import {
  GroupTypeRow,
  GroupTypeWithoutGroupsRow,
} from "./components/GroupTypeRow"
import { useApiRead } from "source/shared/SessionApiResource"

type GroupType = {
  id: string
  attributes: {
    name: string
    description: string
    church_center_web_url: string
  }
}

export default function Entry() {
  useQRCode()

  const { state: locationState } = useLocation()
  return (
    <>
      {locationState?.type && <ApplicantAlerts />}
      <Suspense fallback={<Loading />}>
        <MaybeRedirectToOnlyGroupTypePage>
          <GroupTypesList />
        </MaybeRedirectToOnlyGroupTypePage>
      </Suspense>
    </>
  )
}

function GroupTypesList() {
  const {
    records: groupTypes,
    hasMore,
    loadMore,
  } = useInfiniteLoadingGroupTypes()

  const hasGroups = groupTypes && groupTypes.length > 0

  return hasGroups ? (
    <GroupTypes groupTypes={groupTypes} hasMore={hasMore} loadMore={loadMore} />
  ) : (
    <NoPublishedGroups />
  )
}

function MaybeRedirectToOnlyGroupTypePage({
  children,
}: {
  children: React.ReactNode
}) {
  const { data: groupTypes, meta: groupTypesMeta } =
    useFirstPageOfGroupTypes() as {
      data: GroupType[]
      meta: { total_count: number }
    }

  const {
    meta: { authenticated },
  } = useSession()

  const totalGroupTypeCount = groupTypesMeta?.total_count || 0
  const membershipUrl = authenticated ? `/groups/v2/me/groups?per_page=1` : null
  const { meta: membershipsMeta } = useApiRead(membershipUrl) as {
    meta: { total_count: number }
  }

  const hasOnlyOneGroupType = totalGroupTypeCount === 1
  const hasMemberships = membershipsMeta?.total_count > 0

  const shouldRedirect =
    hasOnlyOneGroupType && (!authenticated || !hasMemberships)

  if (shouldRedirect) {
    return <NavigateToGroupType groupTypes={groupTypes} />
  }

  const shouldShowHeader = authenticated && hasMemberships

  return (
    <>
      {shouldShowHeader && <MyGroupsHeader />}
      {children}
    </>
  )
}

function NavigateToGroupType({ groupTypes }: { groupTypes: GroupType[] }) {
  const { church_center_web_url } = groupTypes[0].attributes
  const groupTypePath = new URL(church_center_web_url).pathname

  return <Navigate to={groupTypePath} replace />
}

function NoPublishedGroups() {
  return (
    <div className="ta-c c-tint2 fs-2">
      <div className="mb-1">
        <Icon symbol="churchCenter#groups-o" />
      </div>
      We don&apos;t have any groups available to browse yet
    </div>
  )
}

function GroupTypes({
  groupTypes,
  hasMore,
  loadMore,
}: {
  groupTypes: GroupType[]
  hasMore: boolean
  loadMore: () => void
}) {
  return (
    <InfiniteScroll
      hasMore={hasMore}
      loadMore={loadMore}
      loader={<Loading key="loading" />}
    >
      {groupTypes.map((groupType) => (
        <Suspense
          key={groupType.id}
          fallback={<GroupTypeWithoutGroupsRow groupType={groupType} />}
        >
          <GroupTypeRow groupType={groupType} />
        </Suspense>
      ))}
    </InfiniteScroll>
  )
}
